import {
  AfterViewInit,
  Component, ElementRef, OnInit, ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { IrisButtonTypes } from '@sondermind/iris/button';
import { DropdownOption, NavigationKeys } from '@sondermind/iris/dropdown';
import { IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import { ISelectMenuConfig } from '../../../models/select-menu-config.interface';
import { IOptionCategory } from '../../../models/option-category.interface';
import { sortCategoryOptions } from '../../../helpers/option-sorting-helpers';

@Component({
  selector: 'select-menu',
  templateUrl: './select-menu.component.html',
  styleUrls: ['./select-menu.component.scss']
})
export class SelectMenuComponent<T> implements OnInit, AfterViewInit {
  categorizedOptions: Array<IOptionCategory<T | null>> = [];
  options: Array<DropdownOption<T | null>> = [];
  control = new FormControl<T[]>([], { nonNullable: true });
  label: string = '';
  hasTopSelection: boolean = false;
  showAll: boolean = false;
  compareOptionsFn: (opt1: T, opt2: T) => boolean = (opt1: T, opt2: T) => opt1 === opt2;

  currentFocusedValue: T | null = null;
  currentHoveredValue: T | null = null;

  // button config values
  primaryIrisButtonType = IrisButtonTypes.PRIMARY;
  tertiaryIrisButtonType = IrisButtonTypes.TERTIARY;
  tertiaryIrisIconButtonType = IrisIconButtonTypes.TERTIARY;
  resetButtonType = IrisButtonTypeAttributeOptions.RESET;
  submitButtonType = IrisButtonTypeAttributeOptions.SUBMIT;

  @ViewChild('listbox') listbox!: ElementRef;
  optionHtmlElements: HTMLElement[] = [];

  // This component will allow for mixed arrow key and tab navigation
  // To support them together and keep the focused element consistent,
  // we need to track Shift+Tab and track Shift separately as a held key
  isShiftBeingPressed: boolean = false;

  constructor(
    private irisModalRef: IrisModalRef<SelectMenuComponent<T>, ISelectMenuConfig<T>, T[] | null>,
    private selectMenuElement: ElementRef
  ) {}

  ngOnInit(): void {
    const configData = this.irisModalRef.config.data;

    this.control.setValue(configData?.selections ?? []);
    this.label = configData?.label ?? '';
    this.hasTopSelection = configData?.hasTopSelection ?? false;
    this.categorizedOptions = configData?.categorizedOptions ?? [];
    this.compareOptionsFn = configData?.compareOptionsFn ?? this.compareOptionsFn;

    this.resetOptions();
  }

  ngAfterViewInit(): void {
    this.optionHtmlElements = this.createOptionsHtmlElementsList();

    // Wait for view to update
    setTimeout(() => {
      this.focusElement(this.optionHtmlElements[0]);
      this.currentFocusedValue = this.options[0].value;
    });
  }

  private createOptionsHtmlElementsList(): HTMLElement[] {
    const resultElements: HTMLElement[] = [];
    if (this.listbox) {
      const lists = (this.listbox.nativeElement as HTMLElement).childNodes as NodeList;
      lists.forEach((list: Node) => {
        list.childNodes.forEach((listItem: ChildNode) => {
          const element = listItem as HTMLElement;

          // the header and any comments will not have aria selected values
          if (element.ariaSelected) {
            resultElements.push(element);
          }
        });
      });
    }

    return resultElements;
  }

  /** Undoes any changes that were made to the filter when it was open */
  cancel(): void {
    this.irisModalRef.close(null);
  }

  /** Closes filter with the updated selections */
  confirm(): void {
    this.irisModalRef.close(this.control.value);
  }

  onHoverEnter(value: T): void {
    this.currentHoveredValue = value;
  }

  onHoverLeave(): void {
    this.currentHoveredValue = null;
  }

  resetOptions(): void {
    this.categorizedOptions = sortCategoryOptions(this.categorizedOptions, this.control, this.showAll, false);

    // eslint-disable-next-line arrow-body-style
    this.options = this.categorizedOptions.reduce((prev, curr) => {
      return prev.concat(curr.options);
    }, [] as Array<DropdownOption<T | null>>);
  }

  resetValue(): void {
    this.control.reset();
    const lastVisibleListElement = this.optionHtmlElements[this.optionHtmlElements.length - 1];
    this.focusElement(lastVisibleListElement);
  }

  /** Expands every category's list of options */
  showAllOptions(): void {
    this.showAll = true;
    const indexUponShowingAll = this.optionHtmlElements.length - 1;
    this.resetOptions();

    // Set timeout to ensure these happen after the view updates.
    setTimeout(() => {
      this.optionHtmlElements = this.createOptionsHtmlElementsList();
      this.currentFocusedValue = this.options[indexUponShowingAll].value;
      this.focusElement(this.optionHtmlElements[indexUponShowingAll]);
    }, 0);
  }

  updateMultiFormControl(newValue: T): void {
    const foundOption = this.control.value.find((val: T) => this.compareOptionsFn(val, newValue));
    if (!foundOption) {
      this.control.setValue([...this.control.value, newValue]);
    } else {
      const newSelections = this.control.value.filter((val: T) => !this.compareOptionsFn(val, newValue));
      this.control.setValue(newSelections);
    }
    this.control.markAsTouched();
    this.control.markAsDirty();

    this.currentFocusedValue = newValue;
  }

  onMobileSelectKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    switch (e.key) {
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        if (this.currentFocusedValue) {
          this.updateMultiFormControl(this.currentFocusedValue);
        }
        break;
      case 'Shift':
        this.isShiftBeingPressed = true;
        break;
      case NavigationKeys.ArrowUp:
        this.navigateList(NavigationKeys.ArrowUp);
        break;
      case NavigationKeys.ArrowDown:
        this.navigateList(NavigationKeys.ArrowDown);
        break;
      case NavigationKeys.Tab:
        this.navigateList(NavigationKeys.Tab);
        break;
      case NavigationKeys.Home:
        this.currentFocusedValue = this.options[0].value;
        this.focusElement(this.optionHtmlElements[0]);
        break;
      case NavigationKeys.End:
        this.currentFocusedValue = this.options[this.options.length - 1].value;
        this.focusElement(this.optionHtmlElements[this.optionHtmlElements.length - 1]);
        break;
      default:
        // eslint-disable-next-line no-useless-return
        return;
    }
  }

  onKeyUp(e: KeyboardEvent): void {
    // The only key up event we care about is Shift being released
    if (e.key === 'Shift') {
      this.isShiftBeingPressed = false;
    }
  }

  onCloseButtonKeyDown(e: KeyboardEvent): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      this.cancel();
    }
  }

  onButtonKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Shift') {
      this.isShiftBeingPressed = true;
    }
  }

  onFocusListItem(focusedValue: T): void {
    this.currentFocusedValue = focusedValue;
  }

  private focusElement(elem: HTMLElement): void {
    elem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    elem.focus();
  }

  private navigateList(direction: NavigationKeys.ArrowDown | NavigationKeys.ArrowUp | NavigationKeys.Tab): void {
    const currentItemIndex: number = this.options.findIndex((option) => option.value === this.currentFocusedValue);

    let nextItemIndex: number = -1;

    const isMovingDownWithArrow = direction === NavigationKeys.ArrowDown;
    const isMovingDownWithTab = direction === NavigationKeys.Tab && !this.isShiftBeingPressed;
    const isMovingUpWithArrow = direction === NavigationKeys.ArrowUp;
    const isMovingUpWithTab = direction === NavigationKeys.Tab && this.isShiftBeingPressed;
    const isAtBottomOfVisibleOptions = currentItemIndex === this.optionHtmlElements.length - 1;
    const isAtTopOfVisibleOptions = currentItemIndex === 0;
    const isShowMoreVisible = this.optionHtmlElements.length < this.options.length;

    if (isMovingDownWithArrow || isMovingDownWithTab) {
      if (!isAtBottomOfVisibleOptions) {
        nextItemIndex = currentItemIndex + 1;
        this.focusOnNextElement(nextItemIndex);
      } else if (isShowMoreVisible) {
        this.focusOnShowMore();
      } else if (isMovingDownWithTab) {
        this.focusOnFinalButton();
      }
    } else if (isMovingUpWithArrow || isMovingUpWithTab) {
      if (!isAtTopOfVisibleOptions) {
        nextItemIndex = currentItemIndex - 1;
        this.focusOnNextElement(nextItemIndex);
      } else if (isMovingUpWithTab) {
        this.focusOnCloseButton();
      }
    }
  }

  private focusOnCloseButton(): void {
    const closeButtonElement = (this.selectMenuElement.nativeElement as HTMLElement)
      .querySelector('.close-button .iris-icon-button') as HTMLElement;
    this.focusElement(closeButtonElement);
  }

  private focusOnFinalButton(): void {
    const isAnySelection = !!this.control.value.length;
    const selectMenuHtmlElement = this.selectMenuElement.nativeElement as HTMLElement;

    const targetButton = isAnySelection
      ? selectMenuHtmlElement.querySelector('.clear-selections-button .iris-button') as HTMLElement
      : selectMenuHtmlElement.querySelector('.see-results-button .iris-button') as HTMLElement;

    this.focusElement(targetButton);
  }

  private focusOnNextElement(targetIndex: number): void {
    const nextItemElem = this.optionHtmlElements[targetIndex];
    this.focusElement(nextItemElem);
    this.currentFocusedValue = this.options[targetIndex].value;
  }

  private focusOnShowMore(): void {
    const showMoreButtonElement = (this.selectMenuElement.nativeElement as HTMLElement)
      .querySelector('.show-more-button') as HTMLElement;
    this.focusElement(showMoreButtonElement);
  }
}
